'use client';

import React, { useCallback, useState, useMemo } from 'react';
import { CircularProgress } from 'src/components';
import { PageHeader } from 'src/components/Settings/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import useGetExports from 'src/hooks/exports/useGetExports';
import { ExportHeader, ExportRow } from './components';
import { SortField, SortDirection, Export, ColumnDefinition } from './types';

const columns: ColumnDefinition[] = [
  {
    label: 'Type',
    field: 'type',
    sortable: false,
  },
  {
    label: 'Export',
    field: 'name',
    sortable: false,
  },
  {
    label: 'Status',
    field: 'status',
    sortable: false,
  },
  {
    label: 'Created',
    field: 'created',
    sortable: true,
  },
];

const ExportsDashboard: React.FC = (): React.ReactElement => {
  const [expandedColumnsId, setExpandedColumnsId] = useState<number | null>(
    null
  );

  const [sort, setSort] = useState({
    sort: 'created_at',
    order: 'desc' as SortDirection,
  });

  const { data: exportsData, isLoading } = useGetExports({
    ...sort,
  });

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === 'name') return;

      const apiField = field === 'created' ? 'created_at' : field;

      if (sort.sort === apiField) {
        setSort({
          sort: apiField,
          order: sort.order === 'asc' ? 'desc' : 'asc',
        });
      } else {
        setSort({
          sort: apiField,
          order: 'asc',
        });
      }
    },
    [sort]
  );

  const exports: Export[] = useMemo(() => {
    return exportsData?.results || [];
  }, [exportsData?.results]);

  const handleExpandColumns = useCallback(
    (exportId: number) => {
      setExpandedColumnsId(exportId === expandedColumnsId ? null : exportId);
    },
    [expandedColumnsId]
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={5} className="px-2 py-8 text-center">
        <div className="flex flex-col items-center gap-2 text-neutral-500">
          <Icon
            icon={faMagnifyingGlass}
            className="h-8 w-8 text-neutral-300"
            aria-hidden="true"
          />
          <span>No exports found</span>
        </div>
      </td>
    </tr>
  );

  const renderLoadingOverlay = () => (
    <div className="h-full w-full absolute top-0 left-0">
      <div className="w-full h-full flex items-center justify-center bg-neutral-100/50 backdrop-blur-sm">
        <CircularProgress
          size="sm"
          data-testid="circular-progress"
          aria-label="Loading exports"
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] w-full max-w-7xl mx-auto p-4">
      <div className="flex flex-col gap-4 h-full">
        <PageHeader className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-neutral-900">Exports</h1>
          <p className="text-sm text-neutral-500">
            Download your data exports. Links expire after 24 hours.
          </p>
        </PageHeader>

        <div className="relative flex flex-col flex-1 min-h-0 gap-4">
          <div className="flex-1 border border-neutral-200 rounded-xl bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="h-full flex flex-col">
              <div className="overflow-x-auto flex-1">
                <table
                  className="w-full table-fixed min-h-64"
                  aria-label="Exports table"
                  role="table"
                >
                  <thead className="bg-neutral-50">
                    <tr className="border-b border-neutral-200">
                      {columns.map((column) => (
                        <ExportHeader
                          key={column.field}
                          column={column}
                          currentSortField={
                            sort.sort === 'created_at'
                              ? 'created'
                              : (sort.sort as SortField)
                          }
                          sortDirection={sort.order}
                          onSort={handleSort}
                        />
                      ))}

                      <th
                        className="sticky top-0 bg-neutral-50 px-2 pr-4 py-4 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider z-10 w-24"
                        scope="col"
                        role="columnheader"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {exports.length === 0 && !isLoading
                      ? renderEmptyState()
                      : exports.map((item) => (
                          <ExportRow
                            key={item.id}
                            item={item}
                            isExpanded={expandedColumnsId === item.id}
                            onExpand={handleExpandColumns}
                          />
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {isLoading && renderLoadingOverlay()}
        </div>
      </div>
    </div>
  );
};

export default ExportsDashboard;
